// Presentation helpers for task facts reported by the API: event summaries and measured
// durations. Nothing here estimates or invents a value (RF-OBS-03, RF-UI-12).
import { useEffect, useState } from "react";
import type { EventEnvelope } from "@maestros/contracts";
import { useI18n } from "../i18n/index.tsx";
import type { TaskDurations } from "../api/types.ts";
import { getTimeline } from "../api/endpoints.ts";
import { Button } from "../components/Button.tsx";
import { ErrorBanner, LoadingBlock } from "../components/Feedback.tsx";
import { ClockIcon } from "../components/icons.tsx";

/** Short factual summary of an event envelope from its payload. */
export function summarizeEvent(e: EventEnvelope): string {
  const p = e.payload;
  const parts: string[] = [];
  const from = typeof p["from"] === "string" ? p["from"] : null;
  const to = typeof p["to"] === "string" ? p["to"] : null;
  if (from && to) parts.push(`${from} → ${to}`);
  else if (to) parts.push(`→ ${to}`);
  if (typeof p["action"] === "string") parts.push(p["action"]);
  if (typeof p["reason"] === "string") parts.push(p["reason"]);
  return parts.join(" · ");
}

export function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

/** The task endpoint aggregates state intervals; these are not agent effort measurements. */
export function DurationsView({ durations, compact = false }: { durations: TaskDurations | undefined; compact?: boolean }) {
  const { t } = useI18n();
  if (!durations || (durations.wall_ms === null && durations.effort_ms === null)) return null;
  const precision = t(`precision.${durations.precision}`);
  const running = durations.open_intervals > 0;
  return (
    <span className={compact ? "durations durations--compact" : "durations"} data-testid="task-durations">
      <ClockIcon size={14} />
      {durations.wall_ms !== null ? <span>{t("durations.wall", { value: formatDuration(durations.wall_ms) })}</span> : null}
      {durations.effort_ms !== null ? <span>{t("durations.effort", { value: formatDuration(durations.effort_ms) })}</span> : null}
      <span className="badge">{precision}</span>
      {running ? <span className="subtle">{t("durations.running")}</span> : null}
    </span>
  );
}

/**
 * Task timeline from GET /v1/tasks/{id}/timeline, oldest first, one page at a time: when the server reports more
 * events (`next_cursor`), the reader is told and can load the next page instead of silently seeing only the first.
 */
export function TaskTimeline({ taskId, version }: { taskId: string; version: number }) {
  const { t, formatDateTime } = useI18n();
  const [entries, setEntries] = useState<EventEnvelope[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [retry, setRetry] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getTimeline(taskId)
      .then((r) => {
        if (cancelled) return;
        setEntries(r.items);
        setNext(r.next_cursor);
      })
      .catch((e: unknown) => !cancelled && setError(e));
    return () => {
      cancelled = true;
    };
  }, [taskId, version, retry]);

  const loadMore = () => {
    if (!next) return;
    setLoadingMore(true);
    getTimeline(taskId, next)
      .then((r) => {
        setEntries((prev) => [...(prev ?? []), ...r.items]);
        setNext(r.next_cursor);
      })
      .catch((e: unknown) => setError(e))
      .finally(() => setLoadingMore(false));
  };

  if (error) return <ErrorBanner error={error} onRetry={() => setRetry((n) => n + 1)} title={t("overview.timelineFailed")} />;
  if (entries === null) return <LoadingBlock label={t("common.loading")} lines={2} />;
  if (entries.length === 0) return <p className="subtle">{t("overview.timelineEmpty")}</p>;
  return (
    <>
      <ol className="timeline" data-testid="task-timeline">
        {entries.map((en) => (
          <li key={en.event_id}>
            <span className="mono subtle">{formatDateTime(en.occurred_at)}</span>
            <span className="badge">{en.type}</span>
            <span className="user-content">{summarizeEvent(en)}</span>
          </li>
        ))}
      </ol>
      {next ? (
        <p className="row subtle" data-testid="timeline-more-notice">
          {t("overview.timelineMore", { n: entries.length })}
          <Button size="sm" variant="ghost" loading={loadingMore} onClick={loadMore}>
            {t("obs.loadMore")}
          </Button>
        </p>
      ) : null}
    </>
  );
}
